import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { leaveAPI, userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Clock, CheckCircle2, XCircle, Ban, Filter,
  CalendarOff, ThumbsUp, ThumbsDown, Eye, Search, Edit2, BarChart3,
} from 'lucide-react';

const LEAVE_TYPES = [
  { value: 'casual', label: 'Casual Leave' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'earned', label: 'Earned Leave' },
  { value: 'personal', label: 'Personal Leave' },
  { value: 'wfh', label: 'Work From Home (WFH)' },
  { value: 'comp_off', label: 'Comp-Off (compensatory)' },
];

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500', icon: Ban },
};

const getStatusInfo = (s) => STATUS_CONFIG[s] || STATUS_CONFIG.pending;
const getLeaveTypeLabel = (t) => LEAVE_TYPES.find(l => l.value === t)?.label || t;

const getDayCount = (start, end) => {
  if (!start || !end) return 0;
  const s = new Date(start);
  const e = new Date(end);
  return Math.max(1, Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1);
};

const LeavesPage = () => {
  const { user, isAdmin, isClient } = useAuth();
  usePageTitle('Leave Management');
  const [leaves, setLeaves] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [balances, setBalances] = useState([]); // [{staff_id, staff_name, assigned, used, pending, remaining}]
  const [myBalance, setMyBalance] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialogs
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [isZeroBalanceWarning, setIsZeroBalanceWarning] = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState(null); // {requested, remaining, staffName}
  const [editLimit, setEditLimit] = useState(null); // {staff_id, staff_name, current_limit}
  const [editLimitValue, setEditLimitValue] = useState(12);
  const [calendarSummary, setCalendarSummary] = useState([]);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [selectedLeave, setSelectedLeave] = useState(null);

  // Forms
  const [form, setForm] = useState({ leave_type: 'casual', start_date: '', end_date: '', reason: '', staff_id: '', comp_off_for_date: '' });
  const [actionForm, setActionForm] = useState({ action: 'approve', remarks: '' });
  const [adjustSundayInput, setAdjustSundayInput] = useState('');

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [leavesRes, statsRes, balanceRes, calRes] = await Promise.all([
        leaveAPI.getAll(params),
        leaveAPI.getStats(),
        leaveAPI.getBalance(),
        leaveAPI.getCalendarSummary(calendarYear),
      ]);
      setLeaves(leavesRes.data);
      setStats(statsRes.data);
      setCalendarSummary(calRes.data || []);
      // Balance may be a list (manager/admin) or single object (staff)
      const balData = balanceRes.data;
      if (Array.isArray(balData)) {
        setBalances(balData);
        const own = balData.find(b => b.staff_id === user?.id);
        if (own) setMyBalance(own);
      } else if (balData && balData.staff_id) {
        setMyBalance(balData);
        setBalances([balData]);
      } else {
        setBalances([]);
      }
      if (isAdmin()) {
        try {
          const staffRes = await userAPI.getAll({ role: 'staff' });
          setStaffList(staffRes.data || []);
        } catch { setStaffList([]); }
      }
    } catch {
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, isAdmin, calendarYear, user]);

  useEffect(() => { loadData(); }, [loadData]);

  const checkBalance = () => {
    // WFH and Comp-Off do not consume leave balance
    if (form.leave_type === 'wfh' || form.leave_type === 'comp_off') return true;

    const requestedDays = getDayCount(form.start_date, form.end_date);

    // Determine target balance
    let bal = null;
    let staffName = '';
    if (isAdmin() && form.staff_id) {
      bal = balances.find(b => b.staff_id === form.staff_id);
      staffName = staffList.find(s => s.id === form.staff_id)?.name || 'staff';
    } else if (!isAdmin()) {
      bal = myBalance;
      staffName = user?.name || 'you';
    } else {
      // Admin applying for self — find own balance
      bal = balances.find(b => b.staff_id === user?.id);
      staffName = user?.name || 'you';
    }

    if (!bal) return true; // No balance data — let backend handle

    if (bal.remaining <= 0) {
      setIsZeroBalanceWarning(true);
      return false;
    }

    if (requestedDays > bal.remaining) {
      setInsufficientBalance({
        requested: requestedDays,
        remaining: bal.remaining,
        staffName,
      });
      return false;
    }

    return true;
  };

  const submitLeave = async ({ mode = 'normal' } = {}) => {
    // mode: 'normal' | 'paid' | 'adjust'
    try {
      const payload = {
        leave_type: form.leave_type,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason,
      };
      if (isAdmin() && form.staff_id) payload.staff_id = form.staff_id;
      if (form.leave_type === 'comp_off' && form.comp_off_for_date) {
        payload.comp_off_for_date = form.comp_off_for_date;
      }
      if (mode === 'paid') {
        payload.is_paid_leave = true;
      } else if (mode === 'adjust') {
        payload.is_adjust_leave = true;
        payload.adjust_sunday_pending = true;
      }
      await leaveAPI.create(payload);
      toast.success(
        mode === 'paid'
          ? 'Paid leave submitted (payroll impact — admin review)'
          : mode === 'adjust'
            ? 'Leave submitted — admin will assign the Sunday working date'
            : (isAdmin() && form.staff_id ? 'Leave applied on behalf of staff (auto-approved)' : 'Leave request submitted')
      );
      setIsCreateOpen(false);
      setInsufficientBalance(null);
      setIsZeroBalanceWarning(false);
      setForm({ leave_type: 'casual', start_date: '', end_date: '', reason: '', staff_id: '', comp_off_for_date: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) {
      toast.error('Start and end dates are required');
      return;
    }
    if (form.leave_type === 'comp_off' && !form.comp_off_for_date) {
      toast.error('Please select the extra working date you are compensating for');
      return;
    }
    if (!checkBalance()) return;
    await submitLeave();
  };

  const handleAction = async () => {
    try {
      await leaveAPI.action(selectedLeave.id, actionForm);
      toast.success(actionForm.action === 'approve'
        ? 'Leave approved & synced to roster'
        : 'Leave rejected');
      setIsActionOpen(false);
      setActionForm({ action: 'approve', remarks: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process leave');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this leave request? Approved roster entries will be removed.')) return;
    try {
      await leaveAPI.cancel(id);
      toast.success('Leave cancelled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const openEditLimit = (b) => {
    setEditLimit(b);
    setEditLimitValue(b.assigned || 12);
  };

  const handleSaveLimit = async () => {
    if (!editLimit) return;
    const val = parseInt(editLimitValue);
    if (isNaN(val) || val < 0 || val > 365) {
      toast.error('Leave limit must be 0–365');
      return;
    }
    try {
      await leaveAPI.updateLeaveLimit(editLimit.staff_id, val);
      toast.success(`Leave limit updated to ${val} for ${editLimit.staff_name}`);
      setEditLimit(null);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update limit');
    }
  };

  const openDetail = (leave) => { setSelectedLeave(leave); setAdjustSundayInput(leave?.adjust_sunday_date || ''); setIsDetailOpen(true); };
  const openAction = (leave) => { setSelectedLeave(leave); setActionForm({ action: 'approve', remarks: '' }); setIsActionOpen(true); };

  const handleAssignAdjustSunday = async () => {
    if (!selectedLeave) return;
    if (!adjustSundayInput) {
      toast.error('Please pick a Sunday working date');
      return;
    }
    // Enforce Sunday
    const d = new Date(adjustSundayInput);
    if (d.getDay() !== 0) {
      if (!window.confirm('The selected date is not a Sunday. Continue anyway?')) return;
    }
    try {
      const res = await leaveAPI.assignAdjustSunday(selectedLeave.id, adjustSundayInput);
      toast.success('Sunday working assigned & staff notified');
      setSelectedLeave(res.data);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleClearAdjustSunday = async () => {
    if (!selectedLeave) return;
    try {
      const res = await leaveAPI.assignAdjustSunday(selectedLeave.id, null);
      toast.success('Sunday working cleared');
      setAdjustSundayInput('');
      setSelectedLeave(res.data);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear');
    }
  };

  const filteredLeaves = leaves.filter(l => {
    const term = searchTerm.toLowerCase();
    return (l.staff_name?.toLowerCase().includes(term) ||
            l.leave_type?.toLowerCase().includes(term) ||
            l.reason?.toLowerCase().includes(term));
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Clock className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="leaves-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-400 p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{isAdmin() ? 'Leave Management' : 'My Leaves'}</h1>
            <p className="text-sm text-amber-100">
              {isAdmin() ? 'Review and manage staff leave requests' : 'Request and track your leave'}
            </p>
          </div>
          {!isClient() && (
            <Button onClick={() => setIsCreateOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="request-leave-btn">
              <Plus className="w-4 h-4 mr-2" /> Request Leave
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md bg-gradient-to-br from-amber-50 to-orange-50">
          <CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-amber-700" data-testid="stat-pending">{stats.pending}</p>
            <p className="text-xs text-amber-500 font-medium">Pending</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-50 to-teal-50">
          <CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-emerald-700" data-testid="stat-approved">{stats.approved}</p>
            <p className="text-xs text-emerald-500 font-medium">Approved</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-gradient-to-br from-red-50 to-rose-50">
          <CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-red-700" data-testid="stat-rejected">{stats.rejected}</p>
            <p className="text-xs text-red-500 font-medium">Rejected</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-gradient-to-br from-slate-50 to-gray-100">
          <CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-slate-700" data-testid="stat-total">{stats.total}</p>
            <p className="text-xs text-slate-500 font-medium">Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Leave Balance Summary */}
      {!isClient() && balances.length > 0 && (
        <Card data-testid="leave-balance-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Leave Balance Summary</CardTitle>
            {!isAdmin() && <p className="text-xs text-muted-foreground">View-only — only Super Admin can edit limits</p>}
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead className="text-center">Assigned</TableHead>
                  <TableHead className="text-center">Used</TableHead>
                  <TableHead className="text-center">Pending</TableHead>
                  <TableHead className="text-center">Remaining</TableHead>
                  {isAdmin() && <TableHead className="text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {balances.map(b => (
                  <TableRow key={b.staff_id} data-testid={`balance-row-${b.staff_id}`} className={b.staff_id === user?.id ? 'bg-blue-50/40' : ''}>
                    <TableCell className="font-medium">
                      {b.staff_name}
                      {b.staff_id === user?.id && <Badge variant="outline" className="ml-2 text-[10px]">You</Badge>}
                    </TableCell>
                    <TableCell className="text-center">{b.assigned}</TableCell>
                    <TableCell className="text-center text-amber-700">{b.used}</TableCell>
                    <TableCell className="text-center text-blue-700">{b.pending}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={b.remaining <= 0 ? 'bg-red-100 text-red-700' : b.remaining <= 2 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                        {b.remaining}
                      </Badge>
                    </TableCell>
                    {isAdmin() && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditLimit(b)} data-testid={`edit-limit-${b.staff_id}`} title="Edit Leave Limit">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search leaves..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="leave-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="leave-status-filter">
            <Filter className="w-4 h-4 mr-2" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leave Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Staff</TableHead>
                <TableHead>Leave Type</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reviewed By</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeaves.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin() ? 9 : 8} className="text-center py-8 text-muted-foreground">
                    No leave requests found
                  </TableCell>
                </TableRow>
              ) : filteredLeaves.map(leave => {
                const si = getStatusInfo(leave.status);
                const Icon = si.icon;
                return (
                  <TableRow key={leave.id} data-testid={`leave-row-${leave.id}`}>
                    <TableCell className="font-medium">
                      {leave.staff_name || '-'}
                      {leave.staff_id === user?.id && <Badge variant="outline" className="ml-2 text-[10px]">You</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant="outline" className="text-xs w-fit">{getLeaveTypeLabel(leave.leave_type)}</Badge>
                        {leave.is_paid_leave && (
                          <Badge className="text-[10px] bg-rose-100 text-rose-700 w-fit" data-testid={`paid-leave-badge-${leave.id}`}>
                            PAID (Payroll Impact)
                          </Badge>
                        )}
                        {leave.is_adjust_leave && (
                          <Badge className="text-[10px] bg-blue-100 text-blue-700 w-fit" data-testid={`adjust-leave-badge-${leave.id}`}>
                            ADJUST • {leave.adjust_sunday_date ? `Sun ${leave.adjust_sunday_date}` : 'Sunday Pending'}
                          </Badge>
                        )}
                        {leave.leave_type === 'comp_off' && leave.comp_off_for_date && (
                          <Badge className="text-[10px] bg-emerald-100 text-emerald-700 w-fit">
                            for {leave.comp_off_for_date}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(leave.start_date)}</TableCell>
                    <TableCell className="text-sm">{formatDate(leave.end_date)}</TableCell>
                    <TableCell className="text-sm font-medium">{getDayCount(leave.start_date, leave.end_date)}</TableCell>
                    <TableCell className="text-sm max-w-[150px] truncate">{leave.reason || '-'}</TableCell>
                    <TableCell>
                      <Badge className={si.color}>
                        <Icon className="w-3 h-3 mr-1" /> {si.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{leave.reviewed_by_name || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(leave)} data-testid={`leave-view-${leave.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {isAdmin() && leave.status === 'pending' && (
                          <Button variant="ghost" size="sm" className="text-green-600" onClick={() => openAction(leave)} data-testid={`leave-action-${leave.id}`}>
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                        )}
                        {leave.status !== 'cancelled' && (leave.staff_id === user?.id || isAdmin()) && leave.status !== 'rejected' && (
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleCancel(leave.id)} data-testid={`leave-cancel-${leave.id}`}>
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Leave Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
            <DialogDescription>Submit a leave request for admin approval</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {isAdmin() && (
              <div className="space-y-2">
                <Label>Apply For</Label>
                <Select value={form.staff_id || 'self'} onValueChange={v => setForm(f => ({ ...f, staff_id: v === 'self' ? '' : v }))}>
                  <SelectTrigger data-testid="leave-form-staff"><SelectValue placeholder="Apply for whom?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">Myself</SelectItem>
                    {staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>)}
                  </SelectContent>
                </Select>
                {form.staff_id && (
                  <p className="text-xs text-blue-600">Admin proxy: leave will be auto-approved on behalf of {staffList.find(s => s.id === form.staff_id)?.name}</p>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select value={form.leave_type} onValueChange={v => setForm(f => ({ ...f, leave_type: v }))}>
                <SelectTrigger data-testid="leave-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(lt => <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From *</Label>
                <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} required data-testid="leave-form-start" />
              </div>
              <div className="space-y-2">
                <Label>To *</Label>
                <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} required data-testid="leave-form-end" />
              </div>
            </div>
            {form.start_date && form.end_date && (
              <p className="text-sm text-muted-foreground">
                Duration: <span className="font-medium text-foreground">{getDayCount(form.start_date, form.end_date)} day(s)</span>
              </p>
            )}
            {form.leave_type === 'comp_off' && (
              <div className="space-y-2">
                <Label>Compensating For (Extra Working Date) *</Label>
                <Input
                  type="date"
                  value={form.comp_off_for_date}
                  onChange={e => setForm(f => ({ ...f, comp_off_for_date: e.target.value }))}
                  required
                  data-testid="leave-form-comp-off-date"
                />
                <p className="text-xs text-muted-foreground">
                  Date you worked extra (Sunday, Saturday night, holiday etc.). Comp-off does not consume leave balance.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} rows={3} placeholder="Optional reason for leave..." data-testid="leave-form-reason" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="leave-form-submit">
                <CalendarOff className="w-4 h-4 mr-1" /> Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Leave Details</DialogTitle>
            <DialogDescription>{selectedLeave?.staff_name}'s leave request</DialogDescription>
          </DialogHeader>
          {selectedLeave && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><span className="text-muted-foreground">Status:</span> <Badge className={getStatusInfo(selectedLeave.status).color}>{getStatusInfo(selectedLeave.status).label}</Badge></div>
                <div><span className="text-muted-foreground">Type:</span> <Badge variant="outline">{getLeaveTypeLabel(selectedLeave.leave_type)}</Badge></div>
                <div><span className="text-muted-foreground">From:</span> {formatDate(selectedLeave.start_date)}</div>
                <div><span className="text-muted-foreground">To:</span> {formatDate(selectedLeave.end_date)}</div>
                <div><span className="text-muted-foreground">Days:</span> {getDayCount(selectedLeave.start_date, selectedLeave.end_date)}</div>
                <div><span className="text-muted-foreground">Requested:</span> {formatDate(selectedLeave.created_at)}</div>
              </div>
              {selectedLeave.reason && (
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Reason</p>
                  <p>{selectedLeave.reason}</p>
                </div>
              )}
              {selectedLeave.leave_type === 'comp_off' && selectedLeave.comp_off_for_date && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                  <p className="text-xs font-medium text-emerald-700 mb-1">Comp-Off Compensating For</p>
                  <p className="text-sm">Extra working date: <strong>{selectedLeave.comp_off_for_date}</strong></p>
                  <p className="text-xs text-emerald-600 mt-1">Does not consume leave balance.</p>
                </div>
              )}
              {selectedLeave.is_paid_leave && (
                <div className="bg-rose-50 border border-rose-200 p-3 rounded-lg">
                  <p className="text-xs font-medium text-rose-700 mb-1">Paid Leave — Payroll Impact</p>
                  <p className="text-sm text-rose-800">Balance was exhausted. This leave will be flagged for payroll review — no Sunday-working obligation.</p>
                </div>
              )}
              {selectedLeave.is_adjust_leave && (
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-blue-700">Adjust-Sunday Leave</p>
                    {selectedLeave.adjust_sunday_pending ? (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px]">Sunday Pending</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700 text-[10px]">Assigned</Badge>
                    )}
                  </div>
                  {selectedLeave.adjust_sunday_date && (
                    <p className="text-sm">Scheduled Sunday working: <strong>{selectedLeave.adjust_sunday_date}</strong></p>
                  )}
                  {isAdmin() && (
                    <div className="pt-2 border-t border-blue-200 space-y-2">
                      <Label className="text-xs">Assign Sunday Working Date (admin)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={adjustSundayInput}
                          onChange={e => setAdjustSundayInput(e.target.value)}
                          className="h-8 text-sm"
                          data-testid="adjust-sunday-input"
                        />
                        <Button size="sm" onClick={handleAssignAdjustSunday} data-testid="adjust-sunday-save" className="bg-blue-600 hover:bg-blue-700">
                          Save
                        </Button>
                        {selectedLeave.adjust_sunday_date && (
                          <Button size="sm" variant="outline" onClick={handleClearAdjustSunday}>Clear</Button>
                        )}
                      </div>
                      <p className="text-[10px] text-blue-600">Pick a Sunday — staff will be notified and a roster entry will be auto-created for that day.</p>
                    </div>
                  )}
                </div>
              )}
              {selectedLeave.review_remarks && (
                <div className={`p-3 rounded-lg border ${selectedLeave.status === 'approved' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-xs font-medium mb-1 ${selectedLeave.status === 'approved' ? 'text-green-700' : 'text-red-700'}`}>
                    {selectedLeave.status === 'approved' ? 'Approval' : 'Rejection'} Remarks
                  </p>
                  <p>{selectedLeave.review_remarks}</p>
                  <p className="text-xs text-muted-foreground mt-1">By: {selectedLeave.reviewed_by_name} on {formatDate(selectedLeave.reviewed_at)}</p>
                </div>
              )}
              {selectedLeave.status === 'approved' && selectedLeave.roster_entry_ids?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                  <p className="text-xs font-medium text-blue-700 mb-1">Roster Sync</p>
                  <p className="text-sm">{selectedLeave.roster_entry_ids.length} roster entries created for leave period</p>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                {isAdmin() && selectedLeave.status === 'pending' && (
                  <Button size="sm" onClick={() => { setIsDetailOpen(false); openAction(selectedLeave); }}>
                    <ThumbsUp className="w-4 h-4 mr-1" /> Review
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Dialog (Approve/Reject) */}
      <Dialog open={isActionOpen} onOpenChange={setIsActionOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review Leave Request</DialogTitle>
            <DialogDescription>
              {selectedLeave?.staff_name} - {getLeaveTypeLabel(selectedLeave?.leave_type)} ({selectedLeave?.start_date} to {selectedLeave?.end_date})
            </DialogDescription>
          </DialogHeader>
          {selectedLeave && (
            <div className="space-y-4">
              {selectedLeave.reason && (
                <div className="bg-muted p-3 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Staff's Reason</p>
                  <p className="text-sm">{selectedLeave.reason}</p>
                </div>
              )}
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm">
                <p className="font-medium text-blue-700">Duration: {getDayCount(selectedLeave.start_date, selectedLeave.end_date)} day(s)</p>
                <p className="text-xs text-blue-600 mt-1">Approving will auto-create leave entries in the Roster calendar</p>
              </div>
              <div className="space-y-2">
                <Label>Action</Label>
                <Select value={actionForm.action} onValueChange={v => setActionForm(f => ({ ...f, action: v }))}>
                  <SelectTrigger data-testid="leave-action-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approve">Approve</SelectItem>
                    <SelectItem value="reject">Reject</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Textarea value={actionForm.remarks} onChange={e => setActionForm(f => ({ ...f, remarks: e.target.value }))} rows={2} placeholder="Optional remarks..." data-testid="leave-action-remarks" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsActionOpen(false)}>Cancel</Button>
            <Button
              onClick={handleAction}
              variant={actionForm.action === 'approve' ? 'default' : 'destructive'}
              data-testid="leave-action-submit"
            >
              {actionForm.action === 'approve'
                ? <><ThumbsUp className="w-4 h-4 mr-1" /> Approve & Sync Roster</>
                : <><ThumbsDown className="w-4 h-4 mr-1" /> Reject</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Monthly/Yearly Leave Calendar Summary */}
      {!isClient() && (
        <Card data-testid="leave-calendar-summary">
          <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              <CardTitle className="text-base">Leave Calendar — {calendarYear}</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">Year:</Label>
              <Select value={String(calendarYear)} onValueChange={v => setCalendarYear(parseInt(v))}>
                <SelectTrigger className="w-28 h-8" data-testid="calendar-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {calendarSummary.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No leave data for {calendarYear}</p>
            ) : (
              <Table className="min-w-[1100px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white z-10">Staff</TableHead>
                    {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                      <TableHead key={m} className="text-center text-xs">{m}</TableHead>
                    ))}
                    <TableHead className="text-center text-xs font-bold">Total ({calendarYear})</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {calendarSummary.map(s => (
                    <TableRow key={s.staff_id} data-testid={`calendar-row-${s.staff_id}`}>
                      <TableCell className="font-medium text-sm sticky left-0 bg-white z-10">
                        {s.staff_name}
                        {s.staff_id === user?.id && <Badge variant="outline" className="ml-2 text-[10px]">You</Badge>}
                      </TableCell>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                        const v = s.months?.[m] || 0;
                        return (
                          <TableCell key={m} className={`text-center text-xs font-medium ${v > 0 ? 'bg-amber-50 text-amber-800' : 'text-muted-foreground'}`}>
                            {v || '-'}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center font-bold">
                        <Badge className={s.total > 0 ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-500'}>
                          {s.total} day{s.total === 1 ? '' : 's'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {calendarSummary.length > 0 && (
              <p className="text-xs text-muted-foreground mt-3">
                Each cell shows approved <strong>leave days</strong> taken in that month. WFH (Work From Home) is tracked in Attendance and is <strong>not</strong> shown here — WFH does not consume leave balance.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Leave Limit Dialog */}
      <Dialog open={!!editLimit} onOpenChange={() => setEditLimit(null)}>
        <DialogContent className="max-w-sm" data-testid="edit-limit-dialog">
          <DialogHeader>
            <DialogTitle>Set Leave Limit</DialogTitle>
            <DialogDescription>Set the maximum annual leave allocation for {editLimit?.staff_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="p-2 bg-amber-50 rounded">
                <p className="text-amber-700">Used</p>
                <p className="font-bold text-amber-900">{editLimit?.used || 0}</p>
              </div>
              <div className="p-2 bg-blue-50 rounded">
                <p className="text-blue-700">Pending</p>
                <p className="font-bold text-blue-900">{editLimit?.pending || 0}</p>
              </div>
              <div className="p-2 bg-emerald-50 rounded">
                <p className="text-emerald-700">Current Limit</p>
                <p className="font-bold text-emerald-900">{editLimit?.assigned || 0}</p>
              </div>
            </div>
            <div>
              <Label>New Max Leave Limit (days/year)</Label>
              <Input
                type="number"
                min="0"
                max="365"
                value={editLimitValue}
                onChange={e => setEditLimitValue(e.target.value)}
                data-testid="edit-limit-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                After update: {editLimitValue || 0} - {(editLimit?.used || 0) + (editLimit?.pending || 0)} = <strong>{Math.max(0, (parseInt(editLimitValue) || 0) - (editLimit?.used || 0) - (editLimit?.pending || 0))}</strong> remaining
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLimit(null)}>Cancel</Button>
            <Button onClick={handleSaveLimit} data-testid="save-limit-btn">Save Limit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Insufficient Balance Warning Dialog */}
      <Dialog open={!!insufficientBalance} onOpenChange={() => setInsufficientBalance(null)}>
        <DialogContent className="max-w-lg" data-testid="insufficient-balance-warning">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Clock className="w-5 h-5" /> Insufficient Leave Balance
            </DialogTitle>
            <DialogDescription>
              {insufficientBalance?.staffName} has only <strong className="text-red-600">{insufficientBalance?.remaining} leave day{insufficientBalance?.remaining === 1 ? '' : 's'}</strong> remaining,
              but this request is for <strong>{insufficientBalance?.requested} day{insufficientBalance?.requested === 1 ? '' : 's'}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="font-medium text-slate-700">Pick how to proceed:</p>
            <div className="grid gap-2">
              <button
                type="button"
                className="text-left border rounded-lg p-3 hover:bg-blue-50 hover:border-blue-300 transition"
                onClick={() => submitLeave({ mode: 'adjust' })}
                data-testid="apply-as-adjust-sunday-insufficient"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-blue-700">Adjust Sunday Leave</span>
                  <Badge className="bg-blue-100 text-blue-700 text-[10px]">Balance Neutral</Badge>
                </div>
                <p className="text-xs text-slate-600 mt-1">Staff will work a Sunday to compensate (admin assigns the Sunday date).</p>
              </button>
              <button
                type="button"
                className="text-left border rounded-lg p-3 hover:bg-rose-50 hover:border-rose-300 transition"
                onClick={() => submitLeave({ mode: 'paid' })}
                data-testid="apply-as-paid-leave-insufficient"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-rose-700">Paid Leave (Payroll Impact)</span>
                  <Badge className="bg-rose-100 text-rose-700 text-[10px]">Salary Deduction</Badge>
                </div>
                <p className="text-xs text-slate-600 mt-1">No Sunday obligation — leave will be flagged for payroll review by admin.</p>
              </button>
            </div>
            <p className="text-xs text-muted-foreground pt-2">Or go back and shorten to {insufficientBalance?.remaining} day(s), or switch to WFH / Comp-Off (no balance used).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInsufficientBalance(null)} data-testid="close-insufficient-warning">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zero Balance Warning Dialog */}
      <Dialog open={isZeroBalanceWarning} onOpenChange={setIsZeroBalanceWarning}>
        <DialogContent className="max-w-lg" data-testid="zero-balance-warning">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" /> No Leave Balance Remaining
            </DialogTitle>
            <DialogDescription>
              Your leave balance is exhausted. Choose how you want to apply this leave so admin can approve it clearly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="font-medium text-slate-700">Pick how to proceed:</p>
            <div className="grid gap-2">
              <button
                type="button"
                className="text-left border rounded-lg p-3 hover:bg-blue-50 hover:border-blue-300 transition"
                onClick={() => submitLeave({ mode: 'adjust' })}
                data-testid="apply-as-adjust-sunday-zero"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-blue-700">Adjust Sunday Leave</span>
                  <Badge className="bg-blue-100 text-blue-700 text-[10px]">Balance Neutral</Badge>
                </div>
                <p className="text-xs text-slate-600 mt-1">You'll work a Sunday to compensate (admin assigns the exact Sunday date).</p>
              </button>
              <button
                type="button"
                className="text-left border rounded-lg p-3 hover:bg-rose-50 hover:border-rose-300 transition"
                onClick={() => submitLeave({ mode: 'paid' })}
                data-testid="apply-as-paid-leave-zero"
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-rose-700">Paid Leave (Payroll Impact)</span>
                  <Badge className="bg-rose-100 text-rose-700 text-[10px]">Salary Deduction</Badge>
                </div>
                <p className="text-xs text-slate-600 mt-1">No Sunday obligation — admin will treat it as a payroll-impact paid leave.</p>
              </button>
            </div>
            <p className="text-xs text-muted-foreground pt-2">Or switch leave type to WFH / Comp-Off (no balance used), or request a leave-limit increase.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsZeroBalanceWarning(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeavesPage;
